import React, { useRef, useState } from "react";
import {
	Badge,
	Button,
	Col,
	Container,
	Modal,
	ModalBody,
	ModalHeader,
	Row,
	UncontrolledTooltip
} from "reactstrap";
import ReportTable from "components/Report";
import { useDispatch, useSelector } from "react-redux";
import * as actions from "./actions";
import * as api from "services/api";
import dayjs from "dayjs";
import ls from "Localization";
import queryString from "query-string";
import { toast } from "react-toastify";
import { FaRegFileExcel } from "react-icons/fa";
import phoneNumber from "helpers/phoneNumber";
import ExportExcel from "helpers/exportExcel";
import ExportExcelForm from "containers/Company/components/ExportExcelForm";
import {
	NOT_SENT_STATUS,
	SENT_CONTRATANTE_STATUS,
	SENT_PRESTADOR_STATUS,
	STATUS_LABEL,
	STATUS_COLOR
} from "./constants";

const MONTHLY_REPORT_EXPORT_BATCH_SIZE = 200;
const MONTHLY_REPORT_EXPORT_LARGE_THRESHOLD = 500;

const SOURCE_LABEL = {
	cron: "Automático",
	manual: "Manual",
	backfill: "Backfill"
};

const EXPORT_RECIPIENT_TYPE_LABEL = {
	contratante: "Contratante",
	prestador: "Prestador"
};

const EXPORT_COLUMNS = [
	"Nome da Empresa Contratante",
	"ID da Empresa Contratante",
	"Status do Relatório",
	"Executado em",
	"Período Inicial",
	"Período Final",
	"Origem",
	"Nome da Empresa Prestadora",
	"Nome",
	"ID do Usuário",
	"Email",
	"Recebeu Email?",
	"Telefone",
	"Recebeu Mensagem?",
	"Tipo do Usuário"
];

function formatExportBoolean(value) {
	if (value === null || value === undefined) return "-";
	return value ? "Sim" : "Não";
}

function formatExportDate(value) {
	return value ? dayjs(value).format(ls.dateFormatShort) : "-";
}

function buildFilterQueryString(filters) {
	return Object.keys(filters || {}).reduce(
		(query, key) =>
			filters[key] ? query + `&filters[${key}]=${filters[key]}` : query,
		""
	);
}

function formatExportRow(row) {
	return {
		"Nome da Empresa Contratante": row.businessName || "-",
		"ID da Empresa Contratante": row.companyId || "-",
		"Status do Relatório": STATUS_LABEL[row.status] || row.status,
		"Executado em": formatExportDate(row.createdAt),
		"Período Inicial": formatExportDate(row.periodStart),
		"Período Final": formatExportDate(row.periodEnd),
		Origem: row.source ? SOURCE_LABEL[row.source] || row.source : "-",
		"Nome da Empresa Prestadora": row.providerBusinessName || "-",
		Nome: row.recipientName || "-",
		"ID do Usuário": row.recipientUserId || "-",
		Email: row.recipientEmail || "-",
		"Recebeu Email?": formatExportBoolean(row.emailSent),
		Telefone: row.recipientPhoneNumber
			? phoneNumber.format(row.recipientPhoneNumber)
			: "-",
		"Recebeu Mensagem?": formatExportBoolean(row.whatsappSent),
		"Tipo do Usuário": row.recipientType
			? EXPORT_RECIPIENT_TYPE_LABEL[row.recipientType] || row.recipientType
			: "-"
	};
}

function buildDefaultFiltered(search) {
	const query = queryString.parse(search);
	const defaultFiltered = [];

	if (query.businessName)
		defaultFiltered.push({ id: "businessName", value: query.businessName });

	if (query.status) defaultFiltered.push({ id: "status", value: query.status });

	if (query.source) defaultFiltered.push({ id: "source", value: query.source });

	if (query.startDate || query.endDate) {
		defaultFiltered.push({
			id: "createdAt",
			value: {
				startDate: query.startDate || "",
				endDate: query.endDate || ""
			}
		});
	}

	return defaultFiltered;
}

function Monthly({ history, location }) {
	const dispatch = useDispatch();
	const table = useRef(null);
	const defaultFiltered = useRef(buildDefaultFiltered(location.search)).current;

	const { token } = useSelector(s => s.login);
	const state = useSelector(s => s.monthlyReport);

	const [exportModalOpen, setExportModalOpen] = useState(false);
	const [exportProgress, setExportProgress] = useState(0);
	const [exportQuery, setExportQuery] = useState({
		filters: {},
		sort: "createdAt",
		desc: true
	});

	const syncUrl = filters => {
		const query = filters.reduce((acc, { id, value }) => {
			if (!value) return acc;

			if (id === "createdAt") {
				if (value.startDate) acc.startDate = value.startDate;
				if (value.endDate) acc.endDate = value.endDate;
			} else {
				acc[id] = value;
			}

			return acc;
		}, {});

		history.replace({
			pathname: location.pathname,
			search: queryString.stringify(query)
		});
	};

	const handleFetchData = tableState => {
		if (!tableState) return;
		let { page, pageSize, sorted, filtered, toExport, callback } = tableState;

		if (!sorted) sorted = [];
		if (!filtered) filtered = [];

		syncUrl(filtered);

		const filters = filtered.reduce((acc, { id, value }) => {
			if (!value) return acc;

			if (id === "createdAt") {
				if (value.startDate) acc.startDate = value.startDate;
				if (value.endDate) acc.endDate = value.endDate;
			} else {
				acc[id] = value;
			}

			return acc;
		}, {});

		setExportQuery({
			filters,
			sort: sorted[0] ? sorted[0].id : undefined,
			desc: sorted[0] ? sorted[0].desc : undefined
		});

		dispatch(
			actions.getMonthlyReport(
				page * pageSize,
				pageSize,
				sorted[0] ? sorted[0].id : undefined,
				sorted[0] ? sorted[0].desc : undefined,
				filters,
				toExport,
				callback
			)
		);
	};

	const toggleExportModal = () => setExportModalOpen(prev => !prev);

	const handleExport = async type => {
		setExportProgress(0);

		try {
			const { filters, sort, desc } = exportQuery;
			const filterQuery = buildFilterQueryString(filters);

			const fetchPage = async skip => {
				const response = await api.sendGet(
					`/monthly-companies-report/export?skip=${skip}&take=${MONTHLY_REPORT_EXPORT_BATCH_SIZE}&sort=${sort ||
						"createdAt"}&desc=${
						desc === undefined ? true : desc
					}${filterQuery}`,
					{ Authorization: "Bearer " + token }
				);

				const result = await response.json();

				if (!result.isValid) {
					throw new Error("Erro ao exportar relatório mensal");
				}

				return result;
			};

			const first = await fetchPage(0);
			const total = first.params?.count || 0;
			const all = [...first.model];

			if (total > MONTHLY_REPORT_EXPORT_LARGE_THRESHOLD) {
				toast.warning(
					"Essa exportação pode demorar um pouco, aguarde a barra de progresso terminar"
				);
			}

			setExportProgress(total ? Math.round((all.length / total) * 100) : 100);

			for (
				let skip = MONTHLY_REPORT_EXPORT_BATCH_SIZE;
				skip < total;
				skip += MONTHLY_REPORT_EXPORT_BATCH_SIZE
			) {
				const page = await fetchPage(skip);
				all.push(...page.model);
				setExportProgress(Math.round((all.length / total) * 100));
			}

			ExportExcel(
				{
					columns: EXPORT_COLUMNS,
					data: all.map(formatExportRow)
				},
				"Relatorio_Mensal_Contratantes",
				type
			);

			setExportModalOpen(false);
		} catch (error) {
			toast.error("Erro ao exportar relatório mensal");
		}
	};

	const onSubmitExportExcel = data => {
		if (data.exportExcel) handleExport("xlsx");
		if (data.exportCsv) handleExport("csv");
	};

	const statusReport = status => (
		<Badge className="w-100" color={STATUS_COLOR[status] || "danger"}>
			{STATUS_LABEL[status] || status}
		</Badge>
	);

	return (
		<Container className="dashboard">
			<Row>
				<Col md={12}>
					<ReportTable
						manual
						filterable
						tableRef={table}
						title={"Relatório Mensal de Contratantes"}
						count={state.count}
						data={state.items}
						pages={state.pages}
						loading={state.loading.getAll}
						noExport
						onFetchData={handleFetchData}
						onRowClicked={row => history.push(`/reports/monthly/${row._id}`)}
						defaultFiltered={defaultFiltered}
						defaultSorted={[
							{
								id: "createdAt",
								desc: true
							}
						]}
						headerRightComponent={
							<div className="d-flex align-items-center">
								<Button
									outline
									onClick={toggleExportModal}
									id="tooltipExportMonthlyReport"
								>
									<FaRegFileExcel style={{ margin: 0 }} />
								</Button>
								<UncontrolledTooltip target="tooltipExportMonthlyReport">
									Exportar os dados da tabela
								</UncontrolledTooltip>
							</div>
						}
						columns={[
							{
								Header: "Nome da Empresa",
								id: "businessName",
								accessor: "businessName",
								className: "bold-text"
							},
							{
								Header: (
									<span>
										Status do
										<br />
										Relatório
									</span>
								),
								id: "status",
								accessor: c => statusReport(c.status),
								Filter: ({ filter, onChange }) => (
									<select
										onChange={event => onChange(event.target.value)}
										style={{ width: "100%" }}
										value={filter && filter.value ? filter.value : "all"}
									>
										<option value="">Todos</option>
										<option value="success">Sucesso</option>
										<option value="failed">Falha</option>
										<option value={NOT_SENT_STATUS}>Não enviado</option>
										<option value={SENT_CONTRATANTE_STATUS}>
											Enviado Contratantes
										</option>
										<option value={SENT_PRESTADOR_STATUS}>
											Enviado Prestadores
										</option>
									</select>
								),
								width: 110
							},
							{
								Header: <span>Executado em:</span>,
								id: "createdAt",
								accessor: ({ createdAt, status }) =>
									status === NOT_SENT_STATUS || !createdAt
										? "-"
										: dayjs(createdAt).format(ls.dateFormatShort),
								Filter: ({ filter, onChange }) => (
									<div style={{ display: "flex" }}>
										<input
											type="date"
											style={{ width: "50%" }}
											value={(filter && filter.value.startDate) || ""}
											onChange={event =>
												onChange({
													...(filter ? filter.value : {}),
													startDate: event.target.value
												})
											}
										/>
										<input
											type="date"
											style={{ width: "50%" }}
											value={(filter && filter.value.endDate) || ""}
											onChange={event =>
												onChange({
													...(filter ? filter.value : {}),
													endDate: event.target.value
												})
											}
										/>
									</div>
								),
								width: 190
							},
							{
								Header: (
									<span>
										Período do
										<br />
										Relatório
									</span>
								),
								id: "period",
								accessor: ({ periodStart, periodEnd, status }) =>
									status === NOT_SENT_STATUS || !periodStart || !periodEnd
										? "-"
										: `${dayjs(periodStart).format(
												ls.dateFormatShort
										  )} - ${dayjs(periodEnd).format(ls.dateFormatShort)}`,
								width: 180,
								sortable: false,
								filterable: false
							},
							{
								Header: "Origem",
								id: "source",
								accessor: ({ source, status }) =>
									status === NOT_SENT_STATUS || !source
										? "-"
										: SOURCE_LABEL[source] || source,
								Filter: ({ filter, onChange }) => (
									<select
										onChange={event => onChange(event.target.value)}
										style={{ width: "100%" }}
										value={filter && filter.value ? filter.value : "all"}
									>
										<option value="">Todos</option>
										<option value="cron">Automático</option>
										<option value="manual">Manual</option>
										<option value="backfill">Backfill</option>
									</select>
								),
								width: 110
							},
							{
								Header: (
									<span>
										E-mails
										<br />
										Enviados
									</span>
								),
								id: "emailCount",
								accessor: ({ emailCount, status }) =>
									status === NOT_SENT_STATUS ? "-" : emailCount || 0,
								filterable: false,
								width: 90
							},
							{
								Header: (
									<span>
										WhatsApps
										<br />
										Enviados
									</span>
								),
								id: "whatsappCount",
								accessor: ({ whatsappCount, status }) =>
									status === NOT_SENT_STATUS ? "-" : whatsappCount || 0,
								filterable: false,
								width: 90
							}
						]}
					/>
				</Col>
			</Row>

			<Modal
				isOpen={exportModalOpen}
				toggle={toggleExportModal}
				onClosed={() => setExportProgress(0)}
			>
				<ModalHeader
					toggle={toggleExportModal}
					className="card__title bold-text"
				>
					Exportar Relatório
				</ModalHeader>
				<ModalBody className="theme-light">
					<ExportExcelForm
						onSubmit={onSubmitExportExcel}
						progress={exportProgress}
					/>
				</ModalBody>
			</Modal>
		</Container>
	);
}

export default Monthly;
